import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Upload,
  Globe,
  Map,
  Building2,
  Home,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileJson,
} from 'lucide-react';
import { toast } from 'sonner';

type LayerKey = 'countries' | 'states' | 'cities' | 'neighborhoods';

interface UploadSummary {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { code?: string; name?: string; reason: string }[];
}

interface LayerState {
  file: File | null;
  parsed: any | null;
  featureCount: number;
  parsing: boolean;
  parseError: string | null;
  uploading: boolean;
  summary: UploadSummary | null;
}

const EMPTY_LAYER: LayerState = {
  file: null,
  parsed: null,
  featureCount: 0,
  parsing: false,
  parseError: null,
  uploading: false,
  summary: null,
};

const LAYER_META: Record<LayerKey, { icon: any; title: string; hint: string; endpoint: string }> = {
  countries: {
    icon: Globe,
    title: 'Countries',
    hint: 'GeoJSON FeatureCollection. Each feature.properties needs `code` and `name: { ar, en }`. Optional: `iso3`, `phone_code`. Geometry not required.',
    endpoint: '/api/v1/admin/geo-upload/countries',
  },
  states: {
    icon: Map,
    title: 'States / Districts',
    hint: 'Each feature.properties needs `code` and `name: { ar, en }`. Country is resolved from `properties.country_code`, `metadata.country_code`, or the Country code field below.',
    endpoint: '/api/v1/admin/geo-upload/states',
  },
  cities: {
    icon: Building2,
    title: 'Cities / Localities',
    hint: 'Each feature.properties needs `locality_code` (or `code`), `locality_name: { ar, en }`, and `state.code`. Point geometry is auto-buffered to a ~1km polygon.',
    endpoint: '/api/v1/admin/geo-upload/cities',
  },
  neighborhoods: {
    icon: Home,
    title: 'Neighborhoods',
    hint: 'Each feature.properties needs `name: { ar, en }` and `city.code`. Upsert key is (city.code, name.en) — referenced cities must already exist.',
    endpoint: '/api/v1/admin/geo-upload/neighborhoods',
  },
};

const ORDER: LayerKey[] = ['countries', 'states', 'cities', 'neighborhoods'];

export default function AdminGeoUpload() {
  const { post } = useApi({ showErrorToast: true });
  const [layers, setLayers] = useState<Record<LayerKey, LayerState>>({
    countries: { ...EMPTY_LAYER },
    states: { ...EMPTY_LAYER },
    cities: { ...EMPTY_LAYER },
    neighborhoods: { ...EMPTY_LAYER },
  });
  const [statesFallbackCountry, setStatesFallbackCountry] = useState('');

  const setLayer = (key: LayerKey, patch: Partial<LayerState>) =>
    setLayers((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleFileChange = async (key: LayerKey, file: File | null) => {
    if (!file) {
      setLayer(key, { ...EMPTY_LAYER });
      return;
    }
    setLayer(key, {
      file,
      parsed: null,
      featureCount: 0,
      parsing: true,
      parseError: null,
      uploading: false,
      summary: null,
    });
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      if (json?.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
        setLayer(key, {
          parsing: false,
          parseError: 'File must be a GeoJSON FeatureCollection with a features[] array.',
        });
        return;
      }
      setLayer(key, {
        parsed: json,
        featureCount: json.features.length,
        parsing: false,
        parseError: null,
      });
    } catch (err: any) {
      setLayer(key, { parsing: false, parseError: err?.message ?? 'Invalid JSON' });
    }
  };

  const handleUpload = async (key: LayerKey) => {
    const layer = layers[key];
    if (!layer.parsed) {
      toast.error('Choose and parse a file first');
      return;
    }
    setLayer(key, { uploading: true, summary: null });
    try {
      const meta = LAYER_META[key];
      const url =
        key === 'states' && statesFallbackCountry.trim()
          ? `${meta.endpoint}?country_code=${encodeURIComponent(statesFallbackCountry.trim().toUpperCase())}`
          : meta.endpoint;
      const result = (await post(url, layer.parsed)) as UploadSummary;
      setLayer(key, { uploading: false, summary: result });
      toast.success(
        `${meta.title}: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped`,
      );
    } catch {
      setLayer(key, { uploading: false });
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Geo Data Upload</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            Bulk import or update countries, states, cities, and neighborhoods from GeoJSON files.
            Records are matched by code — existing rows are updated, new rows are created.
          </p>
        </div>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Upload order matters</AlertTitle>
          <AlertDescription>
            Upload in this order: <strong>Countries → States → Cities → Neighborhoods</strong>.
            Each layer can reference the one above it (e.g. neighborhoods need their city to exist).
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ORDER.map((key) => {
            const meta = LAYER_META[key];
            const layer = layers[key];
            const Icon = meta.icon;
            return (
              <Card key={key} className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold">{meta.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{meta.hint}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`file-${key}`} className="text-sm">
                    GeoJSON file
                  </Label>
                  <Input
                    id={`file-${key}`}
                    type="file"
                    accept=".geojson,.json,application/json,application/geo+json"
                    onChange={(e) => handleFileChange(key, e.target.files?.[0] ?? null)}
                  />
                </div>

                {key === 'states' && (
                  <div className="space-y-2">
                    <Label htmlFor="fallback-country" className="text-sm">
                      Country code (fallback if not in file)
                    </Label>
                    <Input
                      id="fallback-country"
                      placeholder="e.g. SD"
                      value={statesFallbackCountry}
                      onChange={(e) => setStatesFallbackCountry(e.target.value)}
                      maxLength={3}
                      className="uppercase"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 min-h-[2rem]">
                  <div className="text-sm">
                    {layer.parsing && (
                      <span className="inline-flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Parsing…
                      </span>
                    )}
                    {!layer.parsing && layer.parsed && (
                      <span className="inline-flex items-center gap-2 text-foreground">
                        <FileJson className="h-4 w-4 text-primary" />
                        <strong>{layer.featureCount}</strong> features detected
                      </span>
                    )}
                    {layer.parseError && (
                      <span className="text-destructive">{layer.parseError}</span>
                    )}
                  </div>
                  <Button
                    onClick={() => handleUpload(key)}
                    disabled={!layer.parsed || layer.uploading}
                    size="sm"
                  >
                    {layer.uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    <span className="ml-2">Upload</span>
                  </Button>
                </div>

                {layer.summary && (
                  <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Result
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-sm">
                      <Stat label="Total" value={layer.summary.total} />
                      <Stat label="Created" value={layer.summary.created} tone="green" />
                      <Stat label="Updated" value={layer.summary.updated} tone="blue" />
                      <Stat label="Skipped" value={layer.summary.skipped} tone={layer.summary.skipped ? 'amber' : undefined} />
                    </div>
                    {layer.summary.errors.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-destructive">
                          {layer.summary.errors.length} error(s)
                        </summary>
                        <ul className="mt-2 space-y-1 max-h-40 overflow-auto pr-2">
                          {layer.summary.errors.map((e, i) => (
                            <li key={i} className="font-mono">
                              {(e.code || e.name) && (
                                <span className="text-foreground">[{e.code ?? e.name}] </span>
                              )}
                              {e.reason}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </PageContainer>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'green' | 'blue' | 'amber';
}) {
  const color =
    tone === 'green'
      ? 'text-green-600'
      : tone === 'blue'
        ? 'text-blue-600'
        : tone === 'amber'
          ? 'text-amber-600'
          : 'text-foreground';
  return (
    <div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
